/**
 * Product Form Dialog Component - Extracted from AdminPage
 * Handles product creation and editing
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Loader2, X } from 'lucide-react';

// Constants
const COUNTRIES = [
  // Europe
  'España', 'Francia', 'Alemania', 'Italia', 'Portugal', 'UK', 'Irlanda', 'Países Bajos', 'Bélgica', 'Suiza', 'Austria',
  'Polonia', 'Suecia', 'Noruega', 'Dinamarca', 'Finlandia', 'Grecia', 'República Checa', 'Hungría', 'Rumania', 'Bulgaria',
  'Croacia', 'Eslovaquia', 'Eslovenia', 'Serbia', 'Ucrania', 'Rusia', 'Turquía', 'Estonia', 'Letonia', 'Lituania',
  // Americas
  'USA', 'Canadá', 'México', 'Argentina', 'Brasil', 'Colombia', 'Chile', 'Perú', 'Venezuela', 'Ecuador', 'Bolivia',
  'Paraguay', 'Uruguay', 'Costa Rica', 'Panamá', 'Guatemala', 'Honduras', 'El Salvador', 'Nicaragua', 'Cuba',
  'República Dominicana', 'Puerto Rico', 'Jamaica',
  // Asia
  'China', 'Japón', 'Corea del Sur', 'India', 'Indonesia', 'Malasia', 'Singapur', 'Tailandia', 'Vietnam', 'Filipinas',
  'Pakistán', 'Bangladesh', 'Taiwán', 'Hong Kong', 'Israel', 'Emiratos Árabes', 'Arabia Saudita', 'Qatar', 'Kuwait',
  'Irán', 'Irak', 'Jordania', 'Líbano',
  // Africa
  'Marruecos', 'Egipto', 'Sudáfrica', 'Nigeria', 'Kenia', 'Ghana', 'Túnez', 'Argelia', 'Etiopía', 'Tanzania',
  // Oceania
  'Australia', 'Nueva Zelanda',
  // Special
  'Global', 'Internacional', 'Latinoamérica', 'Europa', 'Asia', 'África', 'Oriente Medio'
];
const LANGUAGES = [
  // Major European
  'Español', 'English', 'Français', 'Deutsch', 'Italiano', 'Português', 'Nederlands', 'Polski', 'Русский',
  'Ελληνικά', 'Türkçe', 'Română', 'Magyar', 'Čeština', 'Svenska', 'Norsk', 'Dansk', 'Suomi', 'Українська',
  // Asian
  '日本語', '中文', '한국어', 'हिन्दी', 'العربية', 'עברית', 'ไทย', 'Tiếng Việt', 'Bahasa Indonesia', 'Bahasa Melayu',
  // Other
  'Català', 'Euskara', 'Galego'
];
const LINK_TYPES = ['Dofollow', 'Nofollow', 'Sponsored'];
const LINK_DURATIONS = ['Permanente', '1 Año', '2 Años', '3 Años'];
const AD_LABELS = ['SI', 'NO'];
const REGULAR_CATEGORIES = ['Actualidad', 'Cultura', 'Economía', 'Finanzas', 'Lifestyle', 'Marketing', 'Negocios', 'Salud', 'Tecnología', 'Viajes'];
const SENSITIVE_CATEGORIES = ['Casino', 'Apuestas', 'Cripto', 'CBD', 'Adultos'];

export const ProductFormDialog = ({
  open,
  onOpenChange,
  editingProduct,
  productForm,
  setProductForm,
  onSave,
  saving = false,
  dbCategories = { regular: [], sensitive: [] },
  setCategoryDialogOpen,
  language,
  texts,
}) => {
  const cleanWebsiteUrl = (url) => {
    return url.replace(/^(https?:\/\/)?(www\.)?/i, '').replace(/\/+$/, '').toLowerCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingProduct ? texts.editProduct : texts.addProduct}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Website & Email */}
          <div className="col-span-2">
            <Label>{texts.website} <span className="text-red-500">*</span></Label>
            <Input
              value={productForm.website}
              onChange={(e) => setProductForm({ ...productForm, website: cleanWebsiteUrl(e.target.value) })}
              placeholder="example.com (sin http:// ni www.)"
            />
          </div>

          <div className="col-span-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={productForm.web_email}
              onChange={(e) => setProductForm({ ...productForm, web_email: e.target.value })}
              placeholder="contacto@example.com"
            />
          </div>

          {/* Country & Language */}
          <div>
            <Label>{texts.country} <span className="text-red-500">*</span></Label>
            <Select value={productForm.country} onValueChange={(v) => setProductForm({ ...productForm, country: v })}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'es' ? 'Seleccionar país' : 'Select country'} />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{language === 'es' ? 'Idioma' : 'Language'} <span className="text-red-500">*</span></Label>
            <Select value={productForm.language} onValueChange={(v) => setProductForm({ ...productForm, language: v })}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'es' ? 'Seleccionar idioma' : 'Select language'} />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Link Type & Duration */}
          <div>
            <Label>{language === 'es' ? 'Tipo Enlace' : 'Link Type'} <span className="text-red-500">*</span></Label>
            <Select value={productForm.link_type} onValueChange={(v) => setProductForm({ ...productForm, link_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LINK_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{language === 'es' ? 'Duración' : 'Duration'} <span className="text-red-500">*</span></Label>
            <Select value={productForm.link_duration} onValueChange={(v) => setProductForm({ ...productForm, link_duration: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LINK_DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Ad Label & Site Type */}
          <div>
            <Label>{language === 'es' ? 'Etiqueta (Sponsored)' : 'Ad Label'} <span className="text-red-500">*</span></Label>
            <Select value={productForm.ad_label} onValueChange={(v) => setProductForm({ ...productForm, ad_label: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {AD_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{language === 'es' ? 'Tipo Medio' : 'Site Type'} <span className="text-red-500">*</span></Label>
            <Select value={productForm.site_type} onValueChange={(v) => setProductForm({ ...productForm, site_type: v })}>
              <SelectTrigger>
                <SelectValue placeholder={language === 'es' ? 'Seleccionar' : 'Select'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Periódicos">Periódicos</SelectItem>
                <SelectItem value="Web/Blog">Web/Blog</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Categories Section */}
          <div className="col-span-2 border-t pt-4 mt-2">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">{language === 'es' ? 'Categorías' : 'Categories'}</h4>
              <Button variant="outline" size="sm" onClick={() => setCategoryDialogOpen(true)}>
                <Plus className="h-3 w-3 mr-1" /> {language === 'es' ? 'Gestionar' : 'Manage'}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'es' ? 'Categorías Regulares' : 'Regular Categories'}</Label>
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-2 border rounded">
                  {(dbCategories.regular.length > 0 ? dbCategories.regular : REGULAR_CATEGORIES.map(c => ({name: c}))).map(cat => {
                    const catName = cat.name || cat;
                    const currentCats = productForm.category ? productForm.category.split(',').map(c => c.trim()).filter(c => c) : [];
                    const isChecked = currentCats.includes(catName);
                    return (
                      <label key={catName} className={`flex items-center gap-1 text-xs cursor-pointer px-2 py-1 rounded ${isChecked ? 'bg-green-100 border border-green-300' : 'bg-slate-50'}`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const currentCategories = productForm.category ? productForm.category.split(',').map(c => c.trim()).filter(c => c) : [];
                            const newCategories = e.target.checked 
                              ? [...currentCategories, catName]
                              : currentCategories.filter(c => c !== catName);
                            setProductForm(prev => ({ ...prev, category: newCategories.join(', ') }));
                          }}
                          className="rounded"
                        />
                        {catName}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-orange-600">{language === 'es' ? 'Temáticas Sensibles' : 'Sensitive Topics'}</Label>
                <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto p-2 border border-orange-200 rounded">
                  {[
                    { key: 'casino', es: 'Casino', en: 'Casino' },
                    { key: 'gambling', es: 'Apuestas', en: 'Gambling' },
                    { key: 'cbd', es: 'CBD', en: 'CBD' },
                    { key: 'cannabis', es: 'Cannabis', en: 'Cannabis' },
                    { key: 'cryptocurrency', es: 'Criptomonedas', en: 'Cryptocurrency' },
                    { key: 'sex', es: 'Sexo', en: 'Sex' },
                    { key: 'erotic', es: 'Erótico', en: 'Erotic' },
                    { key: 'porn', es: 'Porno', en: 'Porn' },
                    { key: 'vpn', es: 'VPN', en: 'VPN' },
                    { key: 'tarot', es: 'Tarot', en: 'Tarot' },
                  ].map(topic => {
                    const isChecked = !!productForm[topic.key];
                    const label = language === 'es' ? topic.es : topic.en;
                    return (
                      <label
                        key={topic.key}
                        className={`flex items-center gap-1 text-xs cursor-pointer px-2 py-1 rounded ${isChecked ? 'bg-orange-200 border border-orange-400' : 'bg-orange-50'}`}
                        data-testid={`sensitive-topic-${topic.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            setProductForm(prev => ({ ...prev, [topic.key]: e.target.checked }));
                          }}
                          className="rounded"
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            {productForm.category && (
              <div className="mt-2">
                <span className="text-xs font-medium text-muted-foreground mb-1 block">{language === 'es' ? 'Seleccionadas:' : 'Selected:'}</span>
                <div className="flex flex-wrap gap-1">
                  {productForm.category.split(',').map(cat => cat.trim()).filter(cat => cat).map((cat, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs">
                      {cat}
                      <button
                        type="button"
                        onClick={() => {
                          const cats = productForm.category.split(',').map(c => c.trim()).filter(c => c && c !== cat);
                          setProductForm({ ...productForm, category: cats.join(', ') });
                        }}
                        className="ml-0.5 text-slate-500 hover:text-red-600 hover:bg-red-100 rounded-full p-0.5"
                        title={language === 'es' ? 'Eliminar categoría' : 'Remove category'}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Prices Section */}
          <div className="col-span-2 border-t pt-4 mt-2">
            <h4 className="font-medium text-sm mb-3">{language === 'es' ? 'Precios' : 'Prices'}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-green-700">{language === 'es' ? 'Precio Venta Regular (PVP)' : 'Regular Sale Price'} <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.pvp}
                  onChange={(e) => setProductForm({ ...productForm, pvp: parseFloat(e.target.value) || 0 })}
                  className="border-green-300 focus:border-green-500"
                />
              </div>
              <div>
                <Label className="text-blue-700">{language === 'es' ? 'Precio Compra Regular (PC)' : 'Regular Purchase Price'} <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.pc}
                  onChange={(e) => setProductForm({ ...productForm, pc: parseFloat(e.target.value) || 0 })}
                  className="border-blue-300 focus:border-blue-500"
                />
              </div>
              <div>
                <Label className="text-orange-700">{language === 'es' ? 'Precio Venta Sensible/Premium' : 'Sensitive/Premium Sale Price'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.premium_sale_price}
                  onChange={(e) => setProductForm({ ...productForm, premium_sale_price: parseFloat(e.target.value) || 0 })}
                  className="border-orange-300 focus:border-orange-500"
                />
              </div>
              <div>
                <Label className="text-purple-700">{language === 'es' ? 'Precio Compra Sensible/Premium' : 'Sensitive/Premium Purchase Price'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.premium_purchase_price}
                  onChange={(e) => setProductForm({ ...productForm, premium_purchase_price: parseFloat(e.target.value) || 0 })}
                  className="border-purple-300 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* DR & Traffic */}
          <div>
            <Label>{texts.dr}</Label>
            <Input
              type="number"
              value={productForm.dr}
              onChange={(e) => setProductForm({ ...productForm, dr: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>{texts.traffic}</Label>
            <Input
              type="number"
              value={productForm.organic_traffic}
              onChange={(e) => setProductForm({ ...productForm, organic_traffic: parseInt(e.target.value) || 0 })}
            />
          </div>

          {/* More Info */}
          <div className="col-span-2">
            <Label>{texts.moreInfo}</Label>
            <Textarea
              value={productForm.more_info}
              onChange={(e) => setProductForm({ ...productForm, more_info: e.target.value })}
              rows={2}
            />
          </div>

          {/* Admin-Only Fields */}
          <div className="col-span-2">
            <Label className="text-blue-600">{language === 'es' ? 'Más Info PC (Admin)' : 'More Info PC (Admin)'}</Label>
            <Textarea
              value={productForm.mas_info_pc}
              onChange={(e) => setProductForm({ ...productForm, mas_info_pc: e.target.value })}
              rows={2}
              placeholder={language === 'es' ? 'Ej: 2 enlaces 15€, 3 enlaces 20€' : 'E.g.: 2 links €15, 3 links €20'}
            />
          </div>

          <div>
            <Label className="text-blue-600">{language === 'es' ? '2º Email (Admin)' : '2nd Email (Admin)'}</Label>
            <Input
              value={productForm.second_email}
              onChange={(e) => setProductForm({ ...productForm, second_email: e.target.value })}
              placeholder={language === 'es' ? 'Email secundario' : 'Secondary email'}
            />
          </div>

          <div>
            <Label className="text-red-600">{language === 'es' ? 'Motivo Eliminación (Admin)' : 'Deletion Reason (Admin)'}</Label>
            <Input
              value={productForm.motivo_eliminacion}
              onChange={(e) => setProductForm({ ...productForm, motivo_eliminacion: e.target.value })}
              placeholder={language === 'es' ? 'Motivo si aplica' : 'Reason if applicable'}
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={productForm.available}
              onCheckedChange={(checked) => setProductForm({ ...productForm, available: checked })}
            />
            <Label>{texts.available}</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingProduct ? texts.save : texts.add}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
